import { normalizeName } from './names.js';
/**
 * Leading-brand extraction for slug-derived titles (client insight, 2026-09-30):
 * competitor titles lead with the brand — "SVR SUN SECURE BLUR SPF50+ 50ML",
 * "AVENE XERACALM ULTRA-RICH BAR EAU THERMALE 100G". Match the first 1–3 tokens
 * (longest n-gram wins) against the KNOWN brand vocabulary (our catalogue's
 * brandNorm values + brand aliases) — never guess a token that isn't a known
 * brand, so "LAIT CORPOREL…" stays brandless instead of inventing "Lait".
 */
/** Build the lookup set: values must be normalizeName-shaped (lowercase, de-accented). */
export function buildBrandVocabulary(brands) {
    const set = new Set();
    for (const brand of brands) {
        const normalized = normalizeName(brand);
        if (normalized !== '')
            set.add(normalized);
    }
    return set;
}
/**
 * Extract the leading brand of a raw title. Returns `[display, normalized]` —
 * display keeps the original casing of the title's tokens ("SVR", "Avene"),
 * normalized is the matching vocabulary form. Null when no leading n-gram is a
 * known brand.
 */
export function extractLeadingBrand(title, vocabulary) {
    const tokens = title.trim().split(/\s+/).filter((t) => t !== '');
    if (tokens.length === 0 || vocabulary.size === 0)
        return null;
    const prefixTokens = [];
    for (let take = 1; take <= Math.min(3, tokens.length); take++) {
        prefixTokens.push(tokens[take - 1] ?? '');
        const normalized = normalizeName(prefixTokens.join(' '));
        if (normalized === '')
            continue;
        if (vocabulary.has(normalized)) {
            return { display: prefixTokens.join(' '), normalized };
        }
    }
    return null;
}
/**
 * Canonical brand resolution: map common variations and supplier forms to their
 * base parent canonical normalized key.
 */
export function canonicalParentBrand(brand) {
    const raw = brand.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (raw === 'aderma')
        return 'aderma';
    const norm = normalizeName(brand);
    if (norm === 'derma')
        return 'aderma';
    if (norm === 'roge cavailles' || norm === 'roge-cavailles')
        return 'cavailles';
    if (norm === 'laboratoires svr' || norm === 'laboratoire svr')
        return 'svr';
    if (norm === 'eau thermale avene' || norm === 'eau-thermale-avene')
        return 'avene';
    return norm;
}
/**
 * Metadata for sub-brand ranges and product lines, specifying the human-friendly
 * display label and canonical parent brand.
 */
export const SUB_BRAND_METADATA = {
    // Caudalie
    'vinoperfect': { label: 'Vinoperfect', parent: 'caudalie' },
    'resveratrol lift': { label: 'Resveratrol-Lift', parent: 'caudalie' },
    'premier cru': { label: 'Premier Cru', parent: 'caudalie' },
    'the des vignes': { label: 'Thé des Vignes', parent: 'caudalie' },
    'vinosource hydra': { label: 'Vinosource-Hydra', parent: 'caudalie' },
    'vinosource': { label: 'Vinosource', parent: 'caudalie' },
    'vinoclean': { label: 'Vinoclean', parent: 'caudalie' },
    'vinotherapist': { label: 'Vinotherapist', parent: 'caudalie' },
    'vinosun protect': { label: 'Vinosun Protect', parent: 'caudalie' },
    'vinosun': { label: 'Vinosun', parent: 'caudalie' },
    'vinopure': { label: 'Vinopure', parent: 'caudalie' },
    'drenovigne': { label: 'Dénovigne', parent: 'caudalie' },
    'eau des vignes': { label: 'Eau des Vignes', parent: 'caudalie' },
    'rose de vigne': { label: 'Rose de Vigne', parent: 'caudalie' },
    'fleur de vigne': { label: 'Fleur de Vigne', parent: 'caudalie' },
    // Nuxe
    'huile prodigieuse': { label: 'Huile Prodigieuse', parent: 'nuxe' },
    'reve de miel': { label: 'Rêve de Miel', parent: 'nuxe' },
    'creme fraiche de beaute': { label: 'Crème Fraîche de Beauté', parent: 'nuxe' },
    'creme fraiche': { label: 'Crème Fraîche', parent: 'nuxe' },
    'nuxuriance ultra': { label: 'Nuxuriance Ultra', parent: 'nuxe' },
    'nuxuriance': { label: 'Nuxuriance', parent: 'nuxe' },
    'merveillance lift': { label: 'Merveillance Lift', parent: 'nuxe' },
    'merveillance': { label: 'Merveillance', parent: 'nuxe' },
    'super serum': { label: 'Super Serum [10]', parent: 'nuxe' },
    'nuxe sun': { label: 'Nuxe Sun', parent: 'nuxe' },
    'prodigieux': { label: 'Prodigieux', parent: 'nuxe' },
    'very rose': { label: 'Very Rose', parent: 'nuxe' },
    'hair prodigieux': { label: 'Hair Prodigieux', parent: 'nuxe' },
    'nuxe men': { label: 'Nuxe Men', parent: 'nuxe' },
    'bio beaute': { label: 'Bio-Beauté', parent: 'nuxe' },
    // Cavaillès
    'surgras': { label: 'Surgras', parent: 'cavailles' },
    'savon surgras': { label: 'Savon Surgras', parent: 'cavailles' },
    'soin intime': { label: 'Soin Intime', parent: 'cavailles' },
    'intime': { label: 'Intime', parent: 'cavailles' },
    'deo soin': { label: 'Déo-Soin', parent: 'cavailles' },
    'deodorant': { label: 'Déodorant', parent: 'cavailles' },
    'huile de bain': { label: 'Huile de Bain & Douche', parent: 'cavailles' },
    'nutrissance': { label: 'Nutrissance', parent: 'cavailles' },
    'dermo uht': { label: 'Dermo-U.H.T.', parent: 'cavailles' },
    'sebactiv': { label: 'Sébactiv', parent: 'cavailles' },
    // Bioderma
    'atoderm': { label: 'Atoderm', parent: 'bioderma' },
    'sebium': { label: 'Sébium', parent: 'bioderma' },
    'photoderm': { label: 'Photoderm', parent: 'bioderma' },
    'sensibio': { label: 'Sensibio', parent: 'bioderma' },
    'crealine': { label: 'Créaline', parent: 'bioderma' },
    'node': { label: 'Nodé', parent: 'bioderma' },
    'hydrabio': { label: 'Hydrabio', parent: 'bioderma' },
    'cicabio': { label: 'Cicabio', parent: 'bioderma' },
    'pigmentbio': { label: 'Pigmentbio', parent: 'bioderma' },
    'abcderm': { label: 'ABCDerm', parent: 'bioderma' },
    'matricium': { label: 'Matricium', parent: 'bioderma' },
    // Avène
    'cicalfate': { label: 'Cicalfate', parent: 'avene' },
    'cleanance': { label: 'Cleanance', parent: 'avene' },
    'xeracalm': { label: 'XeraCalm A.D', parent: 'avene' },
    'hydrance': { label: 'Hydrance', parent: 'avene' },
    'tolerance': { label: 'Tolérance', parent: 'avene' },
    'triacneal': { label: 'TriAcnéal', parent: 'avene' },
    'antirougeurs': { label: 'Antirougeurs', parent: 'avene' },
    'couvrance': { label: 'Couvrance', parent: 'avene' },
    'd pigment': { label: 'D-Pigment', parent: 'avene' },
    'solaires': { label: 'Solaires', parent: 'avene' },
    'hyaluron activ b3': { label: 'Hyaluron Activ B3', parent: 'avene' },
    'dermabsolu': { label: 'DermAbsolu', parent: 'avene' },
    'physiolift': { label: 'PhysioLift', parent: 'avene' },
    'a oxitive': { label: 'A-Oxitive', parent: 'avene' },
    'trixera': { label: 'TriXéra', parent: 'avene' },
    // La Roche-Posay
    'effaclar': { label: 'Effaclar', parent: 'la roche posay' },
    'anthelios': { label: 'Anthelios', parent: 'la roche posay' },
    'lipikar': { label: 'Lipikar', parent: 'la roche posay' },
    'cicaplast': { label: 'Cicaplast', parent: 'la roche posay' },
    'toleriane': { label: 'Tolériane', parent: 'la roche posay' },
    'hyalu b5': { label: 'Hyalu B5', parent: 'la roche posay' },
    'pure vitamin c': { label: 'Pure Vitamin C', parent: 'la roche posay' },
    'substiane': { label: 'Substiane', parent: 'la roche posay' },
    'retinol b3': { label: 'Rétinol B3', parent: 'la roche posay' },
    'mela b3': { label: 'Mela B3', parent: 'la roche posay' },
    'kerium': { label: 'Kérium', parent: 'la roche posay' },
    'hydraphase': { label: 'Hydraphase', parent: 'la roche posay' },
    // Uriage
    'bariederm': { label: 'Bariéderm', parent: 'uriage' },
    'xemose': { label: 'Xémose', parent: 'uriage' },
    'hyseac': { label: 'Hyséac', parent: 'uriage' },
    'bariesun': { label: 'Bariésun', parent: 'uriage' },
    'roseliane': { label: 'Roséliane', parent: 'uriage' },
    'depiderm': { label: 'Dépiderm', parent: 'uriage' },
    'eau thermale': { label: 'Eau Thermale', parent: 'uriage' },
    'age protect': { label: 'Age Protect', parent: 'uriage' },
    // Ducray
    'anaphase': { label: 'Anaphase+', parent: 'ducray' },
    'keracnyl': { label: 'Keracnyl', parent: 'ducray' },
    'ictyane': { label: 'Ictyane', parent: 'ducray' },
    'kelual': { label: 'Kelual DS', parent: 'ducray' },
    'neoptide': { label: 'Neoptide', parent: 'ducray' },
    'creastim': { label: 'Creastim', parent: 'ducray' },
    'melascreen': { label: 'Melascreen', parent: 'ducray' },
    'dexyane': { label: 'Dexyane', parent: 'ducray' },
    'squanorm': { label: 'Squanorm', parent: 'ducray' },
    'sensinol': { label: 'Sensinol', parent: 'ducray' },
    // Klorane
    'quinine': { label: 'Quinine', parent: 'klorane' },
    'bleuet': { label: 'Bleuet', parent: 'klorane' },
    'avoine': { label: 'Avoine', parent: 'klorane' },
    'capucine': { label: 'Capucine', parent: 'klorane' },
    'camomille': { label: 'Camomille', parent: 'klorane' },
    'mangue': { label: 'Mangue', parent: 'klorane' },
    'ortie': { label: 'Ortie', parent: 'klorane' },
    'pivoine': { label: 'Pivoine', parent: 'klorane' },
    'cupuacu': { label: 'Cupuaçu', parent: 'klorane' },
    'lin': { label: 'Lin', parent: 'klorane' },
    'dattier': { label: 'Dattier du Désert', parent: 'klorane' },
    'petit brin': { label: 'Petit Brin', parent: 'klorane' },
    // Vichy
    'dercos': { label: 'Dercos', parent: 'vichy' },
    'mineral 89': { label: 'Minéral 89', parent: 'vichy' },
    'liftactiv': { label: 'Liftactiv', parent: 'vichy' },
    'neovadiol': { label: 'Neovadiol', parent: 'vichy' },
    'normaderm': { label: 'Normaderm', parent: 'vichy' },
    'capital soleil': { label: 'Capital Soleil', parent: 'vichy' },
    'aqualia thermal': { label: 'Aqualia Thermal', parent: 'vichy' },
    'purete thermale': { label: 'Pureté Thermale', parent: 'vichy' },
    // A-Derma
    'exomega': { label: 'Exomega', parent: 'aderma' },
    'dermalibour': { label: 'Dermalibour+', parent: 'aderma' },
    'biology': { label: 'Biology', parent: 'aderma' },
    'epitheliale': { label: 'Epitheliale A.H', parent: 'aderma' },
    'phys ac': { label: 'Phys-AC', parent: 'aderma' },
    'cytelium': { label: 'Cytélium', parent: 'aderma' },
    'protect': { label: 'Protect', parent: 'aderma' },
    'primalba': { label: 'Primalba', parent: 'aderma' },
    // Filorga
    'time filler': { label: 'Time-Filler', parent: 'filorga' },
    'optim eyes': { label: 'Optim-Eyes', parent: 'filorga' },
    'meso mask': { label: 'Meso-Mask', parent: 'filorga' },
    'hydra hyal': { label: 'Hydra-Hyal', parent: 'filorga' },
    'ncef': { label: 'NCEF', parent: 'filorga' },
    'lift structure': { label: 'Lift-Structure', parent: 'filorga' },
    'global repair': { label: 'Global-Repair', parent: 'filorga' },
    'age purify': { label: 'Age-Purify', parent: 'filorga' },
    'oxygen glow': { label: 'Oxygen-Glow', parent: 'filorga' },
    'skin unify': { label: 'Skin-Unify', parent: 'filorga' },
    // Eucerin
    'hyaluron filler': { label: 'Hyaluron-Filler', parent: 'eucerin' },
    'anti pigment': { label: 'Anti-Pigment', parent: 'eucerin' },
    'aquaphor': { label: 'Aquaphor', parent: 'eucerin' },
    'dermopure': { label: 'DermoPure', parent: 'eucerin' },
    'urearepair': { label: 'UreaRepair', parent: 'eucerin' },
    'ph5': { label: 'pH5', parent: 'eucerin' },
    'atopicontrol': { label: 'AtopiControl', parent: 'eucerin' },
    'dermocapillaire': { label: 'DermoCapillaire', parent: 'eucerin' },
    // ACM
    'sebionex': { label: 'Sébionex', parent: 'acm' },
    'depiwhite': { label: 'Dépiwhite', parent: 'acm' },
    'duolys': { label: 'Duolys', parent: 'acm' },
    'novophane': { label: 'Novophane', parent: 'acm' },
    'boreade': { label: 'Boréade', parent: 'acm' },
    'rosakalm': { label: 'Rosakalm', parent: 'acm' },
    'cicastim': { label: 'Cicastim', parent: 'acm' },
    'sensitelial': { label: 'Sensitélial', parent: 'acm' },
    // Isdin
    'fotoprotector': { label: 'Fotoprotector', parent: 'isdin' },
    'fusion water': { label: 'Fusion Water', parent: 'isdin' },
    'isdinceutics': { label: 'Isdinceutics', parent: 'isdin' },
    'ureadin': { label: 'Ureadin', parent: 'isdin' },
    'eryfotona': { label: 'Eryfotona', parent: 'isdin' },
    'si nails': { label: 'Si-Nails', parent: 'isdin' },
    'bexident': { label: 'Bexident', parent: 'isdin' },
    'nutratopic': { label: 'Nutratopic', parent: 'isdin' },
    'lambdapil': { label: 'Lambdapil', parent: 'isdin' },
    // Topicrem
    'ultra hydratant': { label: 'Ultra-Hydratant', parent: 'topicrem' },
    'da baume': { label: 'DA Baume', parent: 'topicrem' },
    'ac active': { label: 'AC Active Care', parent: 'topicrem' },
    'mela': { label: 'MELA', parent: 'topicrem' },
    'cica': { label: 'CICA', parent: 'topicrem' },
    'calm': { label: 'Calm+', parent: 'topicrem' },
    'hydra': { label: 'Hydra+', parent: 'topicrem' },
    'ur 10': { label: 'UR-10', parent: 'topicrem' },
    // SVR
    'sun secure': { label: 'Sun Secure', parent: 'svr' },
    'sebiaclear': { label: 'Sebiaclear', parent: 'svr' },
    'topialyse': { label: 'Topialyse', parent: 'svr' },
    'clairial': { label: 'Clairial', parent: 'svr' },
    'spirial': { label: 'Spirial', parent: 'svr' },
    'sensifine': { label: 'Sensifine', parent: 'svr' },
    'cicavit': { label: 'Cicavit+', parent: 'svr' },
    'densitium': { label: 'Densitium', parent: 'svr' },
    'physiopure': { label: 'Physiopure', parent: 'svr' },
    'hydraliane': { label: 'Hydraliane', parent: 'svr' },
    'palpebral': { label: 'Palpébral', parent: 'svr' },
    'xerial': { label: 'Xérial', parent: 'svr' },
    'liftiane': { label: 'Liftiane', parent: 'svr' },
    'hydracid': { label: 'Hydracid', parent: 'svr' },
    'ampoule': { label: 'Ampoules', parent: 'svr' },
    // CeraVe
    'sa smoothing': { label: 'SA Smoothing', parent: 'cerave' },
    // Mustela
    'bebe': { label: 'Bébé', parent: 'mustela' },
    'maternite': { label: 'Maternité', parent: 'mustela' },
    // GUM
    'gum kids': { label: 'Kids', parent: 'gum' },
    'gum junior': { label: 'Junior', parent: 'gum' },
    'ortho': { label: 'Ortho', parent: 'gum' },
    // Chicco
    'baby moments': { label: 'Baby Moments', parent: 'chicco' },
    'physioforma': { label: 'PhysioForma', parent: 'chicco' },
    'naturalfeeling': { label: 'NaturalFeeling', parent: 'chicco' },
};
/**
 * Known sub-brand ranges mapped to their parent brand canonical normalized name.
 * All keys and values are in lowercase, de-accented normalized forms.
 * Client rule (2026-10-21): selecting a brand MUST include all sub-brands everywhere in the app.
 */
export const KNOWN_SUB_BRANDS = Object.fromEntries(Object.entries(SUB_BRAND_METADATA).map(([sub, meta]) => [sub, meta.parent]));
/**
 * Returns all human-friendly sub-brand items (key and label) for a given parent brand.
 * Used to render checkboxes in the UI.
 */
export function getSubBrandsForBrand(brand) {
    const norm = canonicalParentBrand(brand);
    if (norm === '')
        return [];
    const items = [];
    const seenLabels = new Set();
    for (const [key, meta] of Object.entries(SUB_BRAND_METADATA)) {
        if (meta.parent === norm) {
            if (!seenLabels.has(meta.label.toLowerCase())) {
                seenLabels.add(meta.label.toLowerCase());
                items.push({ key, label: meta.label });
            }
        }
    }
    return items;
}
/**
 * Returns all known sub-brand normalized names for a given parent brand.
 */
export function getSubBrandNames(brand) {
    const norm = canonicalParentBrand(brand);
    if (norm === '')
        return [];
    const subs = [];
    for (const [sub, parent] of Object.entries(KNOWN_SUB_BRANDS)) {
        if (parent === norm)
            subs.push(sub);
    }
    return subs;
}
/**
 * Pure test: does a candidate product (by brand and/or name) match a selected target brand,
 * including all of its sub-brands (unless specific sub-brands are excluded)?
 *
 * Examples:
 * - candidateBrand 'SVR', targetBrand 'SVR' -> true
 * - candidateBrand 'SVR Sun Secure', targetBrand 'SVR' -> true
 * - candidateBrand 'Sebiaclear', targetBrand 'SVR' -> true (via KNOWN_SUB_BRANDS)
 * - candidateBrand null, candidateName 'SVR Sebiaclear Crème...', targetBrand 'SVR' -> true
 * - candidateBrand 'Laboratoires SVR', targetBrand 'SVR' -> true
 * - candidateBrand 'Caudalie', candidateName 'Vinoperfect Sérum', targetBrand 'Caudalie', excludedSubBrands ['vinoperfect'] -> false
 */
export function isBrandOrSubBrandMatch(candidateBrand, candidateName, targetBrand, excludedSubBrands) {
    if (!targetBrand || targetBrand.trim() === '' || targetBrand === 'all')
        return true;
    const targetNorm = canonicalParentBrand(targetBrand);
    if (targetNorm === '')
        return true;
    const brandNorm = candidateBrand ? normalizeName(candidateBrand) : '';
    const nameNorm = candidateName ? normalizeName(candidateName) : '';
    // 0. Exclusions check: if candidate matches any excluded sub-brand, reject it
    if (excludedSubBrands && excludedSubBrands.length > 0) {
        for (const excluded of excludedSubBrands) {
            const exNorm = normalizeName(excluded);
            if (exNorm === '')
                continue;
            if (brandNorm !== '') {
                if (brandNorm === exNorm ||
                    brandNorm.startsWith(exNorm + ' ') ||
                    brandNorm.startsWith(exNorm + '-') ||
                    brandNorm.startsWith(exNorm + '/')) {
                    return false;
                }
            }
            if (nameNorm !== '') {
                if (nameNorm === exNorm ||
                    nameNorm.startsWith(exNorm + ' ') ||
                    nameNorm.startsWith(exNorm + '-') ||
                    nameNorm.endsWith(' ' + exNorm) ||
                    nameNorm.includes(' ' + exNorm + ' ') ||
                    nameNorm.includes(' ' + exNorm + '-') ||
                    nameNorm.includes('-' + exNorm + ' ') ||
                    nameNorm.includes('-' + exNorm + '-')) {
                    return false;
                }
            }
        }
    }
    // 1. Direct equality on brand
    if (brandNorm !== '') {
        if (brandNorm === targetNorm || canonicalParentBrand(brandNorm) === targetNorm)
            return true;
        // 2. Prefix sub-brand: e.g. 'svr sun secure', 'svr-topialyse', 'svr/sebiaclear'
        if (brandNorm.startsWith(targetNorm + ' ') ||
            brandNorm.startsWith(targetNorm + '-') ||
            brandNorm.startsWith(targetNorm + '/')) {
            return true;
        }
        // 3. Parent brand prefix of candidate (e.g. 'laboratoires svr' contains 'svr')
        if (brandNorm.includes(' ' + targetNorm) || brandNorm.endsWith(' ' + targetNorm)) {
            return true;
        }
        // 4. Known sub-brand lookup dictionary
        const mappedParent = KNOWN_SUB_BRANDS[brandNorm];
        if (mappedParent !== undefined && mappedParent === targetNorm) {
            return true;
        }
    }
    // 5. Product title check: if the product name starts with or features the target brand
    if (nameNorm !== '') {
        if (nameNorm.startsWith(targetNorm + ' ') || nameNorm.startsWith(targetNorm + '-')) {
            return true;
        }
        if (nameNorm.includes(' ' + targetNorm + ' ') || nameNorm.endsWith(' ' + targetNorm)) {
            return true;
        }
        // Check if product name starts with any known sub-brand of this target
        const subBrands = getSubBrandNames(targetBrand);
        for (const sub of subBrands) {
            if (nameNorm === sub ||
                nameNorm.startsWith(sub + ' ') ||
                nameNorm.startsWith(sub + '-') ||
                nameNorm.endsWith(' ' + sub) ||
                nameNorm.includes(' ' + sub + ' ') ||
                nameNorm.includes(' ' + sub + '-')) {
                return true;
            }
        }
    }
    return false;
}
//# sourceMappingURL=brand.js.map